import {
  ArrowPathIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { CalendarIcon, PlayCircleIcon } from '@heroicons/react/24/solid';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { ShareIconButton } from '@/components/actions';
import { Container } from '@/components/legacy-ui';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SPINNER_MOTION } from '@/components/ui/spinner';
import { useBulkCapabilityJob } from '@/hooks/useCapability';
import {
  CapabilitySchedule,
  useCreateCapabilitySchedule,
  useDeleteCapabilitySchedule,
  useListCapabilitySchedules,
  usePauseCapabilitySchedule,
  useResumeCapabilitySchedule,
  useUpdateCapabilitySchedule,
} from '@/hooks/useCapabilitySchedule';
import { useUploadFile } from '@/hooks/useFiles';
import { cn } from '@/lib/utils';
import {
  DynamicTrafficFields,
  type TrafficSource,
} from '@/sections/asset/components/modals/DynamicTrafficFields';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { getSurfaceDisplayLabel } from '@/utils/capability.util';
import { logger } from '@/utils/logger';
import { startCase } from '@/utils/text.util';

import { CapabilityJobStatus } from './components/CapabilityJobStatus';
import { DrawerStep } from './components/DrawerStep';
import {
  ExecutionModeSelector,
  type ScheduleConfig,
} from './components/ExecutionModeSelector';
import { ExistingSchedulesPanel } from './components/ExistingSchedulesPanel';
import { InternalCapabilityCallout } from './components/InternalCapabilityCallout';
import {
  type PipelineCommitFn,
  type PipelineSummary,
  PipelineSummaryProvider,
} from './components/pipelineEditor/PipelineSummaryContext';
import { RunSummary } from './components/RunSummary';
import { ParametersSection } from './components/sections';
import { AgentSelector } from './components/selectors/AgentSelector';
import { TargetSelector } from './components/selectors/TargetSelector';
import { StepRail } from './components/StepRail';
import { ValidationSummary } from './components/ValidationSummary';
import { type WeeklyScheduleState } from './components/WeeklySchedule';
import { useCapabilityDrawerData } from './hooks/useCapabilityDrawerData';
import { useDrawerValidation } from './hooks/useDrawerValidation';
import {
  DeleteScheduleModal,
  EditScheduleModal,
  PauseScheduleModal,
} from './scheduling/modals';
import { createDefaultWeeklySchedule } from './utils/schedule-utils';

/**
 * CapabilityDrawer
 *
 * KEY-BASED PATTERN: Uses capabilityDrawerKey from URL params as the single
 * source of truth. The drawer fetches its own data internally via
 * useCapabilityDrawerData(key), eliminating race conditions from dual state sources.
 *
 * STEP-BASED UI: Linear 3-step flow replacing tab navigation:
 * - Step 1: CONFIGURE - Parameters and agent selection
 * - Step 2: SELECT TARGETS - Target asset selection
 * - Step 3: EXECUTION - Run Now or Schedule for Later
 */
export function CapabilityDrawer() {
  // KEY-BASED PATTERN: Read key from URL params via drawerState
  const { drawerState } = useGlobalState();
  const capabilityKey = drawerState.capabilityDrawerKey || '';

  // Fetch data internally using the key (single source of truth)
  const { capability, isLoading } = useCapabilityDrawerData(capabilityKey);

  const isPraetorianUser = useAuth().isPraetorianUser;
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  // Wizard mode (when the pipeline editor is active) — shows one step
  // at a time with Back/Next/Submit navigation. Resets to 0 on
  // capability switch via the parent's key={capabilityDrawerKey}.
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  // Pipeline editor → drawer broadcast. The editor writes its current
  // preset/cost on every change; we read this for the Run Summary on
  // the Execution step + to block submission when over-budget.
  const [pipelineSummary, setPipelineSummary] =
    useState<PipelineSummary | null>(null);
  // The editor registers its commit handler here on mount. The Run
  // handler calls it just before submitting so the YAML uploaded to
  // S3 matches the doc the cost panel projected against.
  const [pipelineCommit, setPipelineCommit] = useState<PipelineCommitFn | null>(
    null
  );
  const pipelineSummaryCtx = useMemo(
    () => ({
      summary: pipelineSummary,
      setSummary: setPipelineSummary,
      commit: pipelineCommit,
      setCommit: setPipelineCommit,
    }),
    [pipelineSummary, pipelineCommit]
  );
  const [selectedTargets, setSelectedTargets] = useState<
    Record<string, string[]>
  >({});
  const [parameterValues, setParameterValues] = useState<
    Record<string, string>
  >({});
  const [validationErrors, setValidationErrors] = useState<
    Record<string, boolean>
  >({});
  const contentRef = useRef<HTMLDivElement>(null);

  // Dynamic-traffic enrichment (opt-in, constantine only). These stay LOCAL UI
  // state — only ONE config key (traffic_targets OR traffic_file) is sent on
  // submit. State is reset by the key={capabilityDrawerKey} remount on
  // capability switch (same mechanism as parameterValues/selectedTargets) and
  // after a successful run (below).
  const [trafficEnabled, setTrafficEnabled] = useState(false);
  const [trafficSource, setTrafficSource] = useState<TrafficSource>('guard');
  const [selectedWebApps, setSelectedWebApps] = useState<string[]>([]);
  const [uploadedFileKey, setUploadedFileKey] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { mutateAsync: uploadFile } = useUploadFile();

  const handleTrafficUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const key = `capability-inputs/${Date.now()}-${file.name}`;
      await uploadFile({ name: key, content: file });
      setUploadedFileKey(key);
    } catch (err) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      toast.error(
        axiosErr?.response?.data?.message ||
          'Failed to upload traffic file. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  // Add local state for big output checkbox
  const [largeArtifact, setLargeArtifact] = useState<boolean>(false);

  // Execution mode: 'now' or 'schedule'
  const [executionMode, setExecutionMode] = useState<'now' | 'schedule'>('now');

  // Schedule configuration state
  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig>({
    startDate: '',
    endDate: '',
    schedule: createDefaultWeeklySchedule(),
  });

  // Existing schedules panel state
  const [showSchedulesPanel, setShowSchedulesPanel] = useState(false);

  // Schedule modal states
  const [editingSchedule, setEditingSchedule] =
    useState<CapabilitySchedule | null>(null);
  const [deletingSchedule, setDeletingSchedule] =
    useState<CapabilitySchedule | null>(null);
  const [pausingSchedule, setPausingSchedule] =
    useState<CapabilitySchedule | null>(null);

  // Hooks for schedule management
  const { data: allSchedules = [] } = useListCapabilitySchedules();
  const { mutateAsync: createSchedule, isPending: isCreatingSchedule } =
    useCreateCapabilitySchedule();
  const { mutateAsync: deleteSchedule } = useDeleteCapabilitySchedule();
  const { mutateAsync: updateSchedule } = useUpdateCapabilitySchedule();
  const { mutateAsync: pauseSchedule } = usePauseCapabilitySchedule();
  const { mutateAsync: resumeSchedule } = useResumeCapabilitySchedule();
  const { mutateAsync: runJobs, isPending: isRunningJobs } =
    useBulkCapabilityJob();

  // Filter schedules for current capability
  const capabilitySchedules = useMemo(
    () => allSchedules.filter(s => s.capabilityName === capability?.name),
    [allSchedules, capability?.name]
  );

  // KEY-BASED PATTERN: State reset is handled by key={capabilityDrawerKey} in drawers/index.tsx
  // This forces remount when capability changes, automatically resetting all useState hooks.
  // No useEffect needed for state reset - React's key prop pattern handles this cleanly.

  // Derive default parameters from capability (computed, not stored in state)
  const defaultParams = useMemo(() => {
    if (!capability?.parameters) return {};
    return Object.fromEntries(
      capability.parameters
        .filter(p => p.default)
        .map(p => [p.name, p.default as string])
    );
  }, [capability]);

  // Merge default params with user edits (user edits take precedence)
  const mergedParameterValues = useMemo(
    () => ({ ...defaultParams, ...parameterValues }),
    [defaultParams, parameterValues]
  );

  // Compute all target types from capability
  const allTargetTypes = capability?.target?.map(t => t.toLowerCase()) ?? [];

  // Flatten all selected targets across all target types
  const flatSelectedTargets = useMemo(() => {
    return Object.values(selectedTargets).flat();
  }, [selectedTargets]);

  // Use validation hook for centralized validation state
  const { isConfigValid, hasTargets, canExecute, validationSummary } =
    useDrawerValidation({
      capability,
      selectedAgent,
      selectedTargets: flatSelectedTargets,
      parameterValues: mergedParameterValues,
      validationErrors,
    });

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <Container className="flex size-full items-center justify-center">
        <Skeleton />
      </Container>
    );
  }

  if (!capability) {
    return null;
  }

  const isInternalSurface = capability.surface === 'internal';
  const hasInput = !capability.target.includes('noinput');

  // Constantine exposes traffic_file + traffic_targets as raw params. When
  // present, render the guided DynamicTrafficFields UX instead and suppress the
  // raw inputs (filtered out of the capability handed to ParametersSection).
  const hasTrafficFields =
    capability.name === 'constantine' &&
    (capability.parameters ?? []).some(p => p.name === 'traffic_file');

  const parametersSectionCapability = hasTrafficFields
    ? {
        ...capability,
        parameters: (capability.parameters ?? []).filter(
          p => p.name !== 'traffic_file' && p.name !== 'traffic_targets'
        ),
      }
    : capability;

  // Pipeline-editor wizard mode is active when this capability declares
  // a custom_pipeline file param (constantine today). The pipeline
  // editor is always on (no feature flag). Computed at component scope
  // so both the steps renderer and the footer can branch on it.
  const pipelineEditorActive = !!capability.parameters?.some(
    p => p.name === 'custom_pipeline' && p.type === 'file'
  );

  // Pre-computed step descriptors for footer nav (Back/Next/Submit).
  // Mirrors the order/validity logic used by the steps IIFE below.
  const wizardStepDescriptors = (() => {
    if (!pipelineEditorActive) return [] as Array<{ isValid: boolean }>;
    const showTargets = hasInput && isPraetorianUser;
    const descriptors: Array<{ isValid: boolean }> = [];
    if (showTargets) descriptors.push({ isValid: hasTargets });
    descriptors.push({ isValid: isConfigValid });
    if (isPraetorianUser) descriptors.push({ isValid: canExecute });
    return descriptors;
  })();
  const safeWizardIndex = Math.max(
    0,
    Math.min(currentStepIndex, wizardStepDescriptors.length - 1)
  );
  const isLastWizardStep =
    pipelineEditorActive &&
    safeWizardIndex === wizardStepDescriptors.length - 1;
  const isFirstWizardStep = pipelineEditorActive && safeWizardIndex === 0;
  const currentWizardStepValid =
    pipelineEditorActive &&
    wizardStepDescriptors[safeWizardIndex]?.isValid === true;

  // Helper functions for target display labels
  const getTargetLabel = () => {
    if (!capability?.target?.length) return 'targets';
    const target = capability.target[0].toLowerCase();
    if (target === 'addomain') return 'AD Domains';
    if (target === 'repository') return 'Repositories';
    return `${startCase(target)}s`;
  };

  const getTargetLabelForType = (targetType: string) => {
    const t = targetType.toLowerCase();
    if (t === 'addomain') return 'AD Domains';
    if (t === 'repository') return 'Repositories';
    return `${startCase(t)}s`;
  };

  const getSingularTargetLabel = () => {
    if (!capability?.target?.length) return 'target';
    const target = capability.target[0].toLowerCase();
    if (target === 'addomain') return 'AD Domain';
    return startCase(target);
  };

  // Validate all required parameters are filled
  const validateParameters = () => {
    const errors: Record<string, boolean> = {};
    let hasErrors = false;

    capability.parameters?.forEach(param => {
      const value = mergedParameterValues[param.name]?.trim();
      // Check if parameter is optional based on description or has default value
      const isRequired = param.required;
      if (isRequired && !value) {
        errors[param.name] = true;
        hasErrors = true;
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  };

  // Validate a single parameter on blur (real-time validation)
  const handleParameterBlur = (paramName: string) => {
    const param = capability.parameters?.find(p => p.name === paramName);
    if (!param || !param.required) return;

    const value = mergedParameterValues[paramName]?.trim();
    if (!value) {
      setValidationErrors(prev => ({
        ...prev,
        [paramName]: true,
      }));
    }
  };

  // Handler for running capability immediately
  const handleRunCapability = async () => {
    // Validate parameters before execution
    if (!validateParameters()) {
      toast.error('Please fill in all required parameters');
      return;
    }

    // Guided dynamic-traffic gate: when enabled, the user must either pick
    // web apps (guard source) or upload a file (file source).
    if (
      hasTrafficFields &&
      trafficEnabled &&
      ((trafficSource === 'guard' && selectedWebApps.length === 0) ||
        (trafficSource === 'file' && !uploadedFileKey))
    ) {
      toast.error('Select web applications or upload a traffic file');
      return;
    }

    // Commit the pipeline-editor doc to S3 right before submission so
    // the YAML constantine reads matches the doc the cost panel
    // projected against. Editor registers its commit handler via
    // PipelineSummaryContext; null when no pipeline editor is mounted
    // (capabilities that don't take a custom_pipeline param).
    let pipelineSentinel: string | null = null;
    if (pipelineCommit) {
      try {
        pipelineSentinel = await pipelineCommit();
      } catch (err) {
        toast.error(
          err instanceof Error
            ? `Pipeline upload failed: ${err.name || err.message}`
            : 'Pipeline upload failed'
        );
        return;
      }
    }

    try {
      await runJobs(
        (hasInput ? flatSelectedTargets : ['']).map(target => {
          // Build config object - add aegis parameter and client_id for internal surface capabilities
          const config = { ...mergedParameterValues };
          // Overwrite the custom_pipeline param with the freshly-
          // committed sentinel so the in-memory editor state and the
          // backend job agree on the YAML.
          if (pipelineSentinel) {
            config.custom_pipeline = pipelineSentinel;
          }

          // Guided dynamic-traffic: the raw inputs are suppressed, so drop any
          // traffic keys carried from defaults, then send exactly ONE
          // (traffic_targets OR traffic_file) when enrichment is enabled.
          if (hasTrafficFields) {
            delete config.traffic_file;
            delete config.traffic_targets;
            if (trafficEnabled) {
              if (trafficSource === 'guard' && selectedWebApps.length > 0) {
                config.traffic_targets = selectedWebApps.join(',');
              } else if (trafficSource === 'file' && uploadedFileKey) {
                config.traffic_file = `#file#${uploadedFileKey}`;
              }
            }
          }

          if (isInternalSurface && selectedAgent) {
            config.aegis = 'true';
            config.client_id = selectedAgent;
          }
          config.largeArtifact = String(largeArtifact);
          return {
            assetKey: target,
            capabilities: [capability.name],
            config,
          };
        })
      );

      // Clear selected targets and agent (for internal surface) after successful execution
      if (isInternalSurface) {
        setSelectedAgent('');
      }
      setSelectedTargets({});
      // Reset guided dynamic-traffic selections after a successful run.
      setTrafficEnabled(false);
      setTrafficSource('guard');
      setSelectedWebApps([]);
      setUploadedFileKey(null);
      setIsUploading(false);
    } catch (error) {
      // Error handling is done in the useRunCapability hook
      console.error('Error running capability:', error);
    }
  };

  // Handler for saving scheduled execution (creating new schedule)
  const handleScheduleSave = async () => {
    // Validate required fields
    if (!scheduleConfig.startDate) {
      toast.error('Please select a start date');
      return;
    }

    if (isInternalSurface && !selectedAgent) {
      toast.error('Please select an agent');
      return;
    }

    if (hasInput && flatSelectedTargets.length === 0) {
      toast.error('Please select at least one target');
      return;
    }

    // Guided dynamic-traffic gate: when enabled, the user must either pick
    // web apps (guard source) or upload a file (file source).
    if (
      hasTrafficFields &&
      trafficEnabled &&
      ((trafficSource === 'guard' && selectedWebApps.length === 0) ||
        (trafficSource === 'file' && !uploadedFileKey))
    ) {
      toast.error('Select web applications or upload a traffic file');
      return;
    }

    // Mirror the Run-Now flow: commit the in-memory pipeline doc to S3
    // exactly once before creating schedules so the scheduled execution
    // reads the same YAML the cost panel projected against.
    let pipelineSentinel: string | null = null;
    if (pipelineCommit) {
      try {
        pipelineSentinel = await pipelineCommit();
      } catch (err) {
        toast.error(
          err instanceof Error
            ? `Pipeline upload failed: ${err.name || err.message}`
            : 'Pipeline upload failed'
        );
        return;
      }
    }

    try {
      // Convert dates to RFC3339 format
      const startDate = new Date(scheduleConfig.startDate).toISOString();
      const endDate = scheduleConfig.endDate
        ? new Date(scheduleConfig.endDate).toISOString()
        : undefined;

      // Create a schedule for each selected target (matching the pattern used in execution)
      const targets = hasInput ? flatSelectedTargets : [''];
      const schedulePromises = targets.map(targetKey => {
        // Build config object - add aegis and client_id for internal surface capabilities
        const config = { ...mergedParameterValues };
        if (pipelineSentinel) {
          config.custom_pipeline = pipelineSentinel;
        }

        // Guided dynamic-traffic: mirror the Run-Now config-build — drop any
        // traffic keys from defaults, then send exactly ONE when enabled.
        if (hasTrafficFields) {
          delete config.traffic_file;
          delete config.traffic_targets;
          if (trafficEnabled) {
            if (trafficSource === 'guard' && selectedWebApps.length > 0) {
              config.traffic_targets = selectedWebApps.join(',');
            } else if (trafficSource === 'file' && uploadedFileKey) {
              config.traffic_file = `#file#${uploadedFileKey}`;
            }
          }
        }

        if (isInternalSurface && selectedAgent) {
          config.aegis = 'true';
          config.client_id = selectedAgent;
        }

        return createSchedule({
          capabilityName: capability.name,
          clientId: isInternalSurface ? selectedAgent : undefined, // Only include for internal capabilities
          targetKey, // Create one schedule per target
          config, // Credentials provided via parameters (Username, Password, etc.) + aegis/client_id for internal
          weeklySchedule: scheduleConfig.schedule,
          startDate,
          endDate,
        });
      });

      const results = await Promise.allSettled(schedulePromises);
      const failures = results.filter(r => r.status === 'rejected');
      if (failures.length > 0) {
        toast.error(
          `${failures.length} of ${targets.length} schedules failed to create`
        );
      }

      // Reset to Run Now mode after successful creation
      setExecutionMode('now');
      setScheduleConfig({
        startDate: '',
        endDate: '',
        schedule: createDefaultWeeklySchedule(),
      });
    } catch (error) {
      // Error handling is done by the hook
      console.error('Failed to create schedule:', error);
    }
  };

  // ============================================================================
  // Schedule Panel Handlers
  // ============================================================================

  // Handler for confirming schedule deletion
  const handleConfirmDelete = async (scheduleId: string) => {
    try {
      await deleteSchedule(scheduleId);
      toast.success('Schedule deleted successfully');
      setDeletingSchedule(null);
    } catch (error) {
      logger.error('Failed to delete schedule:', error);
      toast.error('Failed to delete schedule');
    }
  };

  // Handler for confirming schedule pause
  const handleConfirmPause = async (scheduleId: string) => {
    try {
      await pauseSchedule(scheduleId);
      toast.success('Schedule paused successfully');
      setPausingSchedule(null);
    } catch (error) {
      logger.error('Failed to pause schedule:', error);
      toast.error('Failed to pause schedule');
    }
  };

  // Handler for resuming a paused schedule
  const handleResumeSchedule = async (schedule: CapabilitySchedule) => {
    if (schedule.status === 'paused') {
      try {
        await resumeSchedule(schedule.scheduleId);
        toast.success('Schedule resumed successfully');
      } catch (error) {
        logger.error('Failed to resume schedule:', error);
        toast.error('Failed to resume schedule');
      }
    }
  };

  // Handler for saving schedule edits
  const handleSaveEdit = async (
    scheduleId: string,
    updates: {
      weeklySchedule: WeeklyScheduleState;
      startDate: string;
      endDate?: string;
    }
  ) => {
    const startDateISO = new Date(updates.startDate).toISOString();
    const endDateISO = updates.endDate
      ? new Date(updates.endDate).toISOString()
      : undefined;

    await updateSchedule({
      scheduleId,
      updates: {
        weeklySchedule: updates.weeklySchedule,
        startDate: startDateISO,
        endDate: endDateISO,
      },
    });
    setEditingSchedule(null);
  };

  // Handler for "Create New Schedule" from the panel (switches to schedule mode)
  const handleCreateNewFromPanel = () => {
    setShowSchedulesPanel(false);
    setExecutionMode('schedule');
  };

  // Get dynamic button label based on mode and target count
  const getButtonLabel = () => {
    const targetCount = flatSelectedTargets.length;
    const targetName =
      targetCount === 1 ? getSingularTargetLabel() : getTargetLabel();

    if (!hasInput) {
      return executionMode === 'now' ? 'Run' : 'Create Schedule';
    }

    if (executionMode === 'now') {
      return `Run on ${targetCount} ${targetName}`;
    }

    return `Create Schedule for ${targetCount}`;
  };

  return (
    <PipelineSummaryProvider value={pipelineSummaryCtx}>
      <Container className="relative size-full overflow-hidden">
        <div className="flex size-full flex-col overflow-hidden">
          {/*
           * Header — anchored layout. A 3-px brand-tinted left rail
           * gives the drawer a visual identity; the title + meta line
           * stack tightly on the left; status and actions cluster
           * compactly on the right. No floating ScanStatusBox card.
           */}
          <div className="border-chariot-stroke bg-chariot-background-primary/40 border-b">
            <div className="relative flex items-start gap-4 px-5 py-4 sm:px-6 sm:py-5">
              {/* Brand-tinted accent rail */}
              <div
                aria-hidden="true"
                className="bg-chariot-brand-primary/60 absolute top-3 bottom-3 left-0 w-[3px] rounded-r-full"
              />

              {/* Title + meta */}
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <h2 className="text-chariot-text-primary truncate text-xl leading-tight font-bold tracking-tight">
                  {capability.title || capability.name}
                </h2>

                <div className="text-chariot-text-secondary flex min-w-0 items-center gap-2 text-sm">
                  <span className="text-chariot-brand-primary shrink-0 text-[10px] font-bold tracking-[0.12em] uppercase">
                    {getSurfaceDisplayLabel(capability.surface)}
                  </span>
                  {capability.description && (
                    <>
                      <span
                        aria-hidden="true"
                        className="text-chariot-text-tertiary shrink-0"
                      >
                        ·
                      </span>
                      <span className="truncate">{capability.description}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Actions cluster */}
              <div className="flex shrink-0 items-center gap-1.5">
                <CapabilityJobStatus capability={capability} compact />
                <ShareIconButton data-testid="share-capability" />
              </div>
            </div>
          </div>

          {/* Content - Scrollable area for steps */}
          <div
            ref={contentRef}
            className="flex-1 space-y-4 overflow-auto p-4 sm:space-y-6 sm:p-6"
          >
            {(() => {
              const showTargets = hasInput && isPraetorianUser;

              // Step body builders. Each is a React node that already
              // contains the DrawerStep wrapper so legacy (non-wizard)
              // mode keeps its current visual treatment.
              const targetsBody = showTargets ? (
                <>
                  <div className="text-chariot-text-secondary mb-2 text-sm">
                    This capability scans:{' '}
                    <span className="font-medium">
                      {allTargetTypes
                        .map(t => getTargetLabelForType(t))
                        .join(' and ')}
                    </span>
                  </div>
                  {allTargetTypes.map(targetType => (
                    <div
                      key={targetType}
                      className={allTargetTypes.length > 1 ? 'mb-4' : ''}
                    >
                      {allTargetTypes.length > 1 && (
                        <div className="text-chariot-text-primary mb-1 text-sm font-medium">
                          {getTargetLabelForType(targetType)}
                        </div>
                      )}
                      <TargetSelector
                        selectedTargets={selectedTargets[targetType] ?? []}
                        onTargetsChange={targets =>
                          setSelectedTargets(prev => ({
                            ...prev,
                            [targetType]: targets,
                          }))
                        }
                        capability={{ ...capability, target: [targetType] }}
                        getTargetLabel={() => getTargetLabelForType(targetType)}
                      />
                    </div>
                  ))}
                  <div className="text-chariot-text-tertiary mt-2 text-sm">
                    {flatSelectedTargets.length}{' '}
                    {flatSelectedTargets.length === 1 ? 'target' : 'targets'}{' '}
                    selected
                  </div>
                </>
              ) : null;

              const configureBody = (
                <>
                  {isInternalSurface && isPraetorianUser && (
                    <div className="mb-4">
                      <InternalCapabilityCallout
                        onLearnMore={() => {
                          window.open(
                            'https://docs.praetorian.com/aegis',
                            '_blank',
                            'noopener,noreferrer'
                          );
                        }}
                      />
                    </div>
                  )}
                  {isInternalSurface && isPraetorianUser && (
                    <div className="mb-4">
                      <AgentSelector
                        selectedAgent={selectedAgent}
                        onAgentChange={setSelectedAgent}
                      />
                    </div>
                  )}
                  <ParametersSection
                    capability={parametersSectionCapability}
                    parameterValues={mergedParameterValues}
                    setParameterValues={setParameterValues}
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                    isPraetorianUser={isPraetorianUser}
                    largeArtifact={largeArtifact}
                    setLargeArtifact={setLargeArtifact}
                    onParameterBlur={handleParameterBlur}
                    assetKeys={flatSelectedTargets}
                  />
                  {hasTrafficFields && (
                    <div className="mt-4 sm:mt-6">
                      <DynamicTrafficFields
                        enabled={trafficEnabled}
                        source={trafficSource}
                        selectedWebApps={selectedWebApps}
                        uploadedFileKey={uploadedFileKey}
                        osint={false}
                        onEnabledChange={setTrafficEnabled}
                        onSourceChange={setTrafficSource}
                        onSelectedWebAppsChange={setSelectedWebApps}
                        onFileUpload={handleTrafficUpload}
                      />
                    </div>
                  )}
                </>
              );

              const targetPreview =
                flatSelectedTargets.length === 1
                  ? flatSelectedTargets[0]
                      .replace(/^#repository#/, '')
                      .split('#')[0]
                  : flatSelectedTargets.length > 1
                    ? `${flatSelectedTargets.length - 1} more`
                    : undefined;
              const singularTargetLabel = (() => {
                const t = allTargetTypes[0]?.toLowerCase() ?? 'target';
                if (t === 'repository') return 'repository';
                if (t === 'addomain') return 'AD domain';
                return t;
              })();

              const executionBody = isPraetorianUser ? (
                <div className="flex flex-col gap-4">
                  {pipelineEditorActive && (
                    <RunSummary
                      capabilityTitle={capability.title || capability.name}
                      targetCount={flatSelectedTargets.length}
                      targetLabel={singularTargetLabel}
                      targetPreview={targetPreview}
                      mode={executionMode}
                      scanMode={mergedParameterValues.scan_mode}
                      goal={mergedParameterValues.goal}
                      pipelineSummary={pipelineSummary}
                    />
                  )}
                  <ExecutionModeSelector
                    mode={executionMode}
                    onModeChange={setExecutionMode}
                    scheduleConfig={scheduleConfig}
                    onScheduleConfigChange={setScheduleConfig}
                    showLargeArtifact={!!capability.largeArtifact}
                    largeArtifact={largeArtifact}
                    onLargeArtifactChange={setLargeArtifact}
                  />
                  {executionMode === 'schedule' &&
                    flatSelectedTargets.length > 1 && (
                      <p className="text-chariot-text-tertiary text-sm">
                        This will create {flatSelectedTargets.length} schedules
                        (one per target)
                      </p>
                    )}
                </div>
              ) : null;

              // Ordered step descriptors. Determines display order, valid
              // states, and which step the wizard nav advances through.
              type StepDescriptor = {
                id: 'targets' | 'configure' | 'execute';
                title: string;
                isValid: boolean;
                body: ReactNode;
              };
              const allSteps: StepDescriptor[] = [];
              const targetsStep: StepDescriptor | null = showTargets
                ? {
                    id: 'targets',
                    title: 'Select Targets',
                    isValid: hasTargets,
                    body: targetsBody,
                  }
                : null;
              const configureStep: StepDescriptor = {
                id: 'configure',
                title: 'Configure',
                isValid: isConfigValid,
                body: configureBody,
              };
              const executionStep: StepDescriptor | null = executionBody
                ? {
                    id: 'execute',
                    title: 'Execution',
                    isValid: canExecute,
                    body: executionBody,
                  }
                : null;

              if (pipelineEditorActive) {
                if (targetsStep) allSteps.push(targetsStep);
                allSteps.push(configureStep);
                if (executionStep) allSteps.push(executionStep);
              } else {
                allSteps.push(configureStep);
                if (targetsStep) allSteps.push(targetsStep);
                if (executionStep) allSteps.push(executionStep);
              }

              // Wizard mode: render only the active step + an indicator
              // strip. Legacy mode: keep the stacked DrawerStep layout.
              if (pipelineEditorActive) {
                const safeIndex = Math.max(
                  0,
                  Math.min(currentStepIndex, allSteps.length - 1)
                );
                return (
                  <>
                    <StepRail
                      steps={allSteps.map(s => ({
                        id: s.id,
                        title: s.title,
                        isValid: s.isValid,
                      }))}
                      currentIndex={safeIndex}
                      onStepClick={i => setCurrentStepIndex(i)}
                    />
                    {/* Step bodies — render ALL mounted, hide non-active
                      via the `hidden` attribute so each step keeps its
                      local React state across Next/Back navigation
                      (the pipeline editor's usePipelineEditorState
                      otherwise resets when its subtree unmounts and
                      re-init captures the preset's default doc again). */}
                    {allSteps.map((step, i) => (
                      <div
                        key={step.id}
                        hidden={i !== safeIndex}
                        className="pt-1"
                      >
                        {step.body}
                      </div>
                    ))}
                  </>
                );
              }

              return allSteps.map((step, i) => (
                <DrawerStep
                  key={step.id}
                  number={(i + 1) as 1 | 2 | 3}
                  title={step.title}
                  isValid={step.isValid}
                >
                  {step.body}
                </DrawerStep>
              ));
            })()}
          </div>

          {/* Sticky Footer - Only for Praetorian users */}
          {isPraetorianUser && (
            <div className="border-chariot-stroke flex flex-col gap-3 border-t p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
              <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                <ValidationSummary
                  canExecute={canExecute}
                  message={validationSummary}
                />
                {/* View Existing Schedules link - only show if schedules exist */}
                {capabilitySchedules.length > 0 && (
                  <button
                    onClick={() => setShowSchedulesPanel(true)}
                    className="text-chariot-primary hover:text-chariot-primary-hover flex items-center gap-1.5 text-sm transition-colors focus:underline focus:outline-hidden"
                    data-testid="view-schedules-link"
                  >
                    <CalendarDaysIcon className="size-4" />
                    <span>
                      View {capabilitySchedules.length} Schedule
                      {capabilitySchedules.length !== 1 ? 's' : ''}
                    </span>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {pipelineEditorActive && !isFirstWizardStep && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCurrentStepIndex(i => Math.max(0, i - 1))}
                    data-testid="wizard-back-button"
                  >
                    <ChevronLeftIcon className="size-4" />
                    Back
                  </Button>
                )}

                {pipelineEditorActive && !isLastWizardStep ? (
                  <Button
                    size="sm"
                    disabled={!currentWizardStepValid}
                    onClick={() =>
                      setCurrentStepIndex(i =>
                        Math.min(wizardStepDescriptors.length - 1, i + 1)
                      )
                    }
                    data-testid="wizard-next-button"
                  >
                    Next
                    <ChevronRightIcon className="size-4" />
                  </Button>
                ) : executionMode === 'now' ? (
                  <Button
                    size="sm"
                    disabled={
                      !canExecute ||
                      isRunningJobs ||
                      !!pipelineSummary?.overBudget ||
                      isUploading
                    }
                    onClick={handleRunCapability}
                    data-testid="run-capability-button"
                    title={
                      pipelineSummary?.overBudget
                        ? `Projected cost exceeds the $${pipelineSummary.budget} per-scan cap`
                        : undefined
                    }
                  >
                    {isRunningJobs ? (
                      <ArrowPathIcon className={cn('size-4', SPINNER_MOTION)} />
                    ) : (
                      <PlayCircleIcon className="size-4" />
                    )}
                    {isRunningJobs ? 'Running...' : getButtonLabel()}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    disabled={
                      !canExecute ||
                      isCreatingSchedule ||
                      !!pipelineSummary?.overBudget ||
                      isUploading
                    }
                    onClick={handleScheduleSave}
                    data-testid="create-schedule-button"
                    title={
                      pipelineSummary?.overBudget
                        ? `Projected cost exceeds the $${pipelineSummary.budget} per-scan cap`
                        : undefined
                    }
                  >
                    {isCreatingSchedule ? (
                      <ArrowPathIcon className={cn('size-4', SPINNER_MOTION)} />
                    ) : (
                      <CalendarIcon className="size-4" />
                    )}
                    {isCreatingSchedule ? 'Creating...' : getButtonLabel()}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Existing Schedules Panel - Slides in from right */}
          <ExistingSchedulesPanel
            capabilityName={capability.title || capability.name}
            capabilityKey={capability.name}
            schedules={capabilitySchedules}
            isOpen={showSchedulesPanel}
            onClose={() => setShowSchedulesPanel(false)}
            onCreateNew={handleCreateNewFromPanel}
            onEdit={setEditingSchedule}
            onPause={setPausingSchedule}
            onResume={handleResumeSchedule}
            onDelete={setDeletingSchedule}
          />
        </div>

        {/* Schedule Modals */}
        <EditScheduleModal
          schedule={editingSchedule}
          isOpen={!!editingSchedule}
          onClose={() => setEditingSchedule(null)}
          onSave={handleSaveEdit}
        />

        <DeleteScheduleModal
          schedule={deletingSchedule}
          isOpen={!!deletingSchedule}
          onClose={() => setDeletingSchedule(null)}
          onConfirm={handleConfirmDelete}
        />

        <PauseScheduleModal
          schedule={pausingSchedule}
          isOpen={!!pausingSchedule}
          onClose={() => setPausingSchedule(null)}
          onConfirm={handleConfirmPause}
        />
      </Container>
    </PipelineSummaryProvider>
  );
}
